import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, Ticket, Clock, AlertTriangle, CheckCircle, BarChart3, ChevronDown } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useUserCheck } from '../hooks/api/useUserCheck'
import { usePermissions } from '../hooks/usePermissions'
import { useTickets, useCreateTicket } from '../hooks/api/useTickets'
import { DashboardLayout } from '../components/layout/DashboardLayout'
import { IssuesEmptyState } from '../components/empty-states/IssuesEmptyState'
import { DateFilterPopover } from '../components/ui/DateFilterPopover'
import { TicketSuccessBanner } from '../components/ui/TicketSuccessBanner'
import { CreateTicketModal, TicketFormData } from '../components/modals/CreateTicketModal'

type TabType = 'all' | 'open' | 'ongoing' | 'closed'

export const EmployeeIssuesPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: userRecord, isLoading, error } = useUserCheck(user?.id || '')
  const { hasPermission } = usePermissions({ userRole: userRecord?.role || null })
  const [dateFilter, setDateFilter] = useState('7d')
  const [activeTab, setActiveTab] = useState<TabType>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [showSuccessBanner, setShowSuccessBanner] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const createTicketMutation = useCreateTicket()

  // Get tickets from Supabase - EMPLOYEE VIEW: Only show tickets created by this employee
  const { data: allTickets = [], isLoading: ticketsLoading } = useTickets(userRecord?.company_id || '')
  const tickets = allTickets.filter(ticket => ticket.created_by_user_id === user?.id)

  // Filter tickets based on active tab
  const filteredTickets = tickets.filter(ticket => {
    if (activeTab === 'open') return ticket.status === 'open'
    if (activeTab === 'ongoing') return ticket.status === 'in_progress'
    if (activeTab === 'closed') return ticket.status === 'closed' || ticket.status === 'resolved'
    return true
  })

  // Calculate stats
  const totalTickets = tickets.length
  const ongoingTickets = tickets.filter(t => t.status === 'in_progress').length
  const openTickets = tickets.filter(t => t.status === 'open').length
  const resolvedTickets = tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length

  const handleIssueClick = (issueId: string) => {
    navigate(`/issues/${issueId}`)
  }

  const handleCreateTicket = async (ticketData: TicketFormData) => {
    if (!userRecord?.company_id || !user?.id) {
      setCreateError('Missing account information needed to submit a ticket.')
      return
    }

    setCreateError(null)

    try {
      await createTicketMutation.mutateAsync({
        ticketData: {
          title: ticketData.subject.trim(),
          description: ticketData.description,
          priority: ticketData.priority,
        },
        companyId: userRecord.company_id,
        createdByUserId: user.id,
      })

      setShowModal(false)
      setShowSuccessBanner(true)
      // Auto-hide banner after 5 seconds
      setTimeout(() => setShowSuccessBanner(false), 5000)
    } catch (err) {
      console.error('Failed to create ticket', err)
      setCreateError('Unable to create ticket. Please try again.')
    }
  }

  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / 60000)

    if (diffInMinutes < 60) return `${diffInMinutes} mins ago`
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`
    return `${Math.floor(diffInMinutes / 1440)} days ago`
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-3 border-gray-400 border-t-transparent border-r-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading issues...</p>
        </div>
      </div>
    )
  }

  if (error || !userRecord) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-600 text-sm font-medium mb-2">❌ User not found in database!</p>
            <p className="text-red-500 text-xs mb-2">This account was created before user record creation was implemented.</p>
            <p className="text-red-500 text-xs">Please create a new account to test the full flow.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <DashboardLayout
      userRole={userRecord.role}
      adminLevel={userRecord.admin_level}
      userName={userRecord.full_name || user?.email}
      userEmail={user?.email}
    >
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Issues</h1>
          {hasPermission('CREATE_TICKETS') && (
            <button
              onClick={() => {
                setCreateError(null)
                setShowModal(true)
              }}
              className="inline-flex items-center px-4 py-3 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Report Issue
            </button>
          )}
        </div>

        {/* Alerts */}
        {showSuccessBanner && (
          <TicketSuccessBanner onClose={() => setShowSuccessBanner(false)} />
        )}
        {createError && (
          <div className="bg-red-50 border border-red-200 text-red-600 text-sm font-medium px-4 py-3 rounded-2xl">
            {createError}
          </div>
        )}

        {/* Search and Filter */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-full text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
          </div>
          <DateFilterPopover
            selectedValue={dateFilter}
            onValueChange={setDateFilter}
          />
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Tickets */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Ticket className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{totalTickets}</p>
                <p className="text-sm text-gray-500 mt-1">Total Tickets</p>
              </div>
            </div>
          </div>

          {/* Ongoing */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{ongoingTickets}</p>
                <p className="text-sm text-gray-500 mt-1">ongoing</p>
              </div>
            </div>
          </div>

          {/* Open */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{openTickets}</p>
                <p className="text-sm text-gray-500 mt-1">Open</p>
              </div>
            </div>
          </div>

          {/* Resolved */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{resolvedTickets}</p>
                <p className="text-sm text-gray-500 mt-1">Resolved</p>
              </div>
            </div>
          </div>
        </div>

        {/* Issues Table */}
        {hasPermission('VIEW_TICKETS') && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Issues</h2>
            </div>

            {/* Tabs */}
            <div className="inline-flex items-center gap-2 mb-6 p-1 bg-gray-50 rounded-full">
              <button
                onClick={() => setActiveTab('all')}
                className={`px-4 py-1 text-sm font-medium rounded-full transition-colors ${
                  activeTab === 'all'
                    ? 'bg-white text-black  border border-zinc-300 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                all
              </button>
              <button
                onClick={() => setActiveTab('open')}
                className={`px-4 py-1 text-sm font-medium rounded-full transition-colors ${
                  activeTab === 'open'
                    ? 'bg-white text-black  border border-zinc-300 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                open
              </button>
              <button
                onClick={() => setActiveTab('ongoing')}
                className={`px-4 py-1 text-sm font-medium rounded-full transition-colors ${
                  activeTab === 'ongoing'
                    ? 'bg-white text-black  border border-zinc-300 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                ongoing
              </button>
              <button
                onClick={() => setActiveTab('closed')}
                className={`px-4 py-1 text-sm font-medium rounded-full transition-colors ${
                  activeTab === 'closed'
                    ? 'bg-white text-black border border-zinc-300 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                closed
              </button>
            </div>

            {ticketsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-3 border-gray-400 border-t-transparent border-r-transparent"></div>
                <span className="ml-2 text-gray-600">Loading issues...</span>
              </div>
            ) : filteredTickets.length > 0 ? (
              <div className="space-y-3">
                {/* Ticket Cards */}
                {filteredTickets.map((ticket) => {
                  const ticketNumber = ticket.id.slice(0, 6)

                  return (
                    <div
                      key={ticket.id}
                      className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-sm transition-shadow"
                    >
                      <div className="flex items-start justify-between gap-4">
                        {/* Left section - Ticket info */}
                        <div className="flex-1 min-w-0">
                          {/* Ticket number and status */}
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-semibold text-gray-900">#{ticketNumber}</span>
                            <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-full">
                              <div className={`w-2 h-2 rounded-full ${
                                ticket.status === 'open' ? 'bg-red-500' :
                                ticket.status === 'in_progress' ? 'bg-orange-500' :
                                'bg-green-500'
                              }`}></div>
                              <span className="text-xs text-gray-600">{ticket.status}</span>
                              <ChevronDown className="w-3 h-3 text-gray-400" />
                            </div>
                          </div>

                          {/* Title */}
                          <h3 className="text-base font-semibold text-gray-900 mb-1 line-clamp-1">
                            {ticket.title}
                          </h3>

                          {/* Description */}
                          <p className="text-sm text-gray-500 line-clamp-2 mb-2">
                            {ticket.description || 'No description provided'}
                          </p>

                          {/* Timestamp */}
                          <div className="flex items-center gap-1 text-xs text-gray-400">
                            <Clock className="w-3 h-3" />
                            <span>{getRelativeTime(ticket.created_at)}</span>
                          </div>
                        </div>

                        {/* Right section - Priority and action */}
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {/* Priority badge */}
                          <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                            ticket.priority === 'high' ? 'bg-red-100 text-red-700' :
                            ticket.priority === 'medium' ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            <span className="capitalize">{ticket.priority}</span>
                            <BarChart3 className="w-3 h-3" />
                          </div>

                          {/* Open ticket button */}
                          <button
                            onClick={() => handleIssueClick(ticket.id)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors whitespace-nowrap"
                          >
                            Open Ticket
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <IssuesEmptyState />
            )}
          </div>
        )}
      </div>

      {/* Create Ticket Modal */}
      <CreateTicketModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleCreateTicket}
        isSubmitting={createTicketMutation.isPending}
      />
    </DashboardLayout>
  )
}
